import React, { useEffect, useRef, useState } from "react";
import { Card } from 'primereact/card';
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import DropdownComponent from "../../components/form-control/drop-down";
import apiFunctions from "../../services/api-service";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { UserIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import Chart from "chart.js/auto";
import { formatDateTime } from "../../utils/HelperFunctions";
import LocationMap, { LocationPoint } from "../../components/common/LocationMap";
import CountryFlag from "../../components/common/CountryFlag";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const hourLabel = (h: number) =>
    h === 0 ? "12a" : h < 12 ? `${h}a` : h === 12 ? "12p" : `${h - 12}p`;

const DAY_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/* Day (x) × Hour (y) activity heatmap — Google popular-times style */
const ActivityHeatmap: React.FC<{ activity: { day: number; hour: number; count: number }[] }> = ({ activity }) => {
    // activity day: 1=Sun ... 7=Sat (mongo $dayOfWeek)
    const grid: number[][] = Array.from({ length: 24 }, () => Array(7).fill(0));
    activity.forEach(a => {
        if (a.hour >= 0 && a.hour < 24 && a.day >= 1 && a.day <= 7) {
            grid[a.hour][a.day - 1] = a.count;
        }
    });
    const max = Math.max(...grid.flat(), 1);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const [tooltip, setTooltip] = useState<{ x: number; y: number; day: number; hour: number; count: number } | null>(null);

    const handleCellMove = (e: React.MouseEvent, d: number, h: number, count: number) => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        setTooltip({
            x: Math.min(Math.max(e.clientX - rect.left, 70), rect.width - 70),
            y: e.clientY - rect.top,
            day: d,
            hour: h,
            count,
        });
    };

    return (
        <div ref={containerRef} style={{ overflowX: "auto", position: "relative" }}>
            <div style={{ minWidth: "320px" }}>
                {/* Day labels (x-axis) */}
                <div style={{ display: "flex", marginLeft: "36px" }}>
                    {DAY_LABELS.map(d => (
                        <div key={d} style={{ flex: 1, textAlign: "center", fontSize: "11px", fontWeight: 700, color: "#374151", paddingBottom: "4px" }}>
                            {d}
                        </div>
                    ))}
                </div>
                {/* Hour rows (y-axis) */}
                {grid.map((row, h) => (
                    <div key={h} style={{ display: "flex", alignItems: "center" }}>
                        <div style={{ width: "36px", fontSize: "10px", fontWeight: 700, color: "#374151", textAlign: "right", paddingRight: "6px" }}>
                            {h % 3 === 0 ? hourLabel(h) : ""}
                        </div>
                        {row.map((count, d) => {
                            const isHovered = tooltip?.day === d && tooltip?.hour === h;
                            return (
                                <div
                                    key={d}
                                    onMouseMove={(e) => handleCellMove(e, d, h, count)}
                                    onMouseLeave={() => setTooltip(null)}
                                    style={{
                                        flex: 1,
                                        height: "13px",
                                        margin: "1px",
                                        borderRadius: "2px",
                                        cursor: "pointer",
                                        outline: isHovered ? "2px solid #1f2937" : "none",
                                        outlineOffset: "-1px",
                                        background: count === 0
                                            ? "#f3f4f6"
                                            : `rgba(42, 143, 158, ${0.15 + 0.85 * (count / max)})`,
                                        transition: "outline 0.05s",
                                    }}
                                />
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Custom tooltip — flips below the cell when too close to the top to fit above */}
            {tooltip && (() => {
                const TOOLTIP_CLEARANCE = 80;
                const flip = tooltip.y < TOOLTIP_CLEARANCE;
                return (
                    <div
                        style={{
                            position: "absolute",
                            left: tooltip.x,
                            top: flip ? tooltip.y + 14 : tooltip.y - 14,
                            transform: flip ? "translate(-50%, 0)" : "translate(-50%, -100%)",
                            background: "#1f2937",
                            color: "#fff",
                            padding: "8px 12px",
                            borderRadius: "8px",
                            fontSize: "12px",
                            pointerEvents: "none",
                            whiteSpace: "nowrap",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                            zIndex: 10,
                            textAlign: "center",
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: "2px" }}>
                            {DAY_FULL[tooltip.day]}, {hourLabel(tooltip.hour)} – {hourLabel((tooltip.hour + 1) % 24)}
                        </div>
                        <div style={{ background: "#fff", color: "#111827", fontWeight: 600, padding: "2px 8px", borderRadius: "4px", marginTop: "4px" }}>
                            {tooltip.count} active user{tooltip.count === 1 ? "" : "s"}
                        </div>
                        {/* arrow */}
                        <div
                            style={{
                                position: "absolute",
                                left: "50%",
                                ...(flip ? { top: "-5px" } : { bottom: "-5px" }),
                                transform: "translateX(-50%) rotate(45deg)",
                                width: "10px",
                                height: "10px",
                                background: "#1f2937",
                            }}
                        />
                    </div>
                );
            })()}
        </div>
    );
};

const DashBoardPage: React.FC = () => {
    const navigate = useNavigate();

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const [countryid, setCountryId] = useState<any>("fb");

    const [selectionOptions] = useState([
        { name: "Last 7 days", id: "7d" },
        { name: "Last month", id: "1m" },
        { name: "Last year", id: "1y" },
        { name: "From Begining", id: "fb" },
    ]);

    const [loading, setLoading] = useState(true);

    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [mobileOnlyUsers, setMobileOnlyUsers] = useState<number>(0);
    const [grandTotalUsers, setGrandTotalUsers] = useState<number>(0);
    const [isRealUsers, setIsRealUsers] = useState<number>(0);
    const [approvedUsers, setApprovedUsers] = useState<number>(0);
    const [newLeads, setNewLeads] = useState<number>(0);

    const [totalBusinesses, setTotalBusinesses] = useState<number>(0);
    const [totalProducts, setTotalProducts] = useState<number>(0);
    const [totalActiveProducts, setTotalActiveProducts] = useState<number>(0);
    const [totalJobs, setTotalJobs] = useState<number>(0);
    const [totalMatrimonialProfiles, setTotalMatrimonialProfiles] = useState<number>(0);

    const [activeUsers30Min, setActiveUsers30Min] = useState<number | null>(null);
    const [activeUsersToday, setActiveUsersToday] = useState<number | null>(null);
    const [playStats, setPlayStats] = useState<{ averageRating: number | null; totalReviews: number | null; installs: string | null }>({ averageRating: null, totalReviews: null, installs: null });

    const [latestLeads, setLatestLeads] = useState<any[]>([]);

    const [activityData, setActivityData] = useState<{ day: number; hour: number; count: number }[]>([]);
    const [activityRange, setActivityRange] = useState<string>("7days");
    const [activityRangeOptions] = useState([
        { name: "Last 7 Days", id: "7days" },
        { name: "Today", id: "today" },
        { name: "Current Week", id: "week" },
    ]);
    const [monthlyData, setMonthlyData] = useState<{ year: number; month: number; count: number }[]>([]);
    const monthlyCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const monthlyChartRef = useRef<Chart | null>(null);

    const [locations, setLocations] = useState<LocationPoint[]>([]);
    const [totalWithLocation, setTotalWithLocation] = useState<number>(0);
    const [locationsLoading, setLocationsLoading] = useState(true);

    // "Top Cities" row click → drills the side panel (not the map, which
    // stays put) into a paginated user list for that city, loaded more on
    // scroll (same threshold pattern as BloodGroupPage's city picker).
    const CITY_USERS_LIMIT = 20;
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const [cityUsers, setCityUsers] = useState<any[]>([]);
    const [cityUsersTotal, setCityUsersTotal] = useState(0);
    const [cityUsersPage, setCityUsersPage] = useState(0);
    const [cityUsersLoading, setCityUsersLoading] = useState(false);
    const [citySearch, setCitySearch] = useState("");

    const fetchCityUsers = async (city: string, page: number, reset: boolean, search: string) => {
        if (cityUsersLoading) return;
        setCityUsersLoading(true);
        try {
            const params = new URLSearchParams({ city, page: String(page), limit: String(CITY_USERS_LIMIT) });
            if (search.trim()) params.set("search", search.trim());
            const response = await apiFunctions.get(`/user/dashboard/locations/users?${params.toString()}`);
            if (response?.data?.success) {
                const newItems = response.data.data || [];
                setCityUsers((prev) => (reset ? newItems : [...prev, ...newItems]));
                setCityUsersTotal(response.data.totalRecords || 0);
                setCityUsersPage(page + 1);
            }
        } catch (error) {
            console.error("Failed to load city users:", error);
        } finally {
            setCityUsersLoading(false);
        }
    };

    const handleCityClick = (city: string) => {
        setSelectedCity(city);
        setCityUsers([]);
        setCityUsersPage(0);
        setCitySearch("");
        fetchCityUsers(city, 0, true, "");
    };

    const handleCitySearchChange = (value: string) => {
        setCitySearch(value);
        setCityUsers([]);
        setCityUsersPage(0);
        if (selectedCity) fetchCityUsers(selectedCity, 0, true, value);
    };

    const handleCityUsersScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.currentTarget;
        const nearBottom = scrollTop + offsetHeight >= scrollHeight - 10;
        const moreToLoad = cityUsers.length < cityUsersTotal;
        if (nearBottom && moreToLoad && !cityUsersLoading && selectedCity) {
            fetchCityUsers(selectedCity, cityUsersPage, false, citySearch);
        }
    };

    const citySidebarContent = selectedCity ? (
        <div className="flex flex-col" style={{ height: "420px" }}>
            <div
                className="flex items-center gap-2 cursor-pointer"
                style={{ flexShrink: 0, padding: "0 0 10px", borderBottom: "1px solid #e5e7eb" }}
                onClick={() => setSelectedCity(null)}
            >
                <ArrowLeftIcon style={{ width: 16, height: 16, color: "#264164" }} />
                <span className="text-sm font-semibold" style={{ color: "#264164" }}>Back</span>
                <span className="text-sm" style={{ color: "#6b7280" }}>— {selectedCity} ({cityUsersTotal.toLocaleString()})</span>
            </div>
            <div style={{ flexShrink: 0, padding: "8px 0 0" }} onClick={(e) => e.stopPropagation()}>
                <PrimeInputText
                    value={citySearch}
                    onChange={(e) => handleCitySearchChange(e.target.value)}
                    placeholder="Search by name or mobile..."
                    className="w-full"
                    style={{ fontSize: "13px" }}
                />
            </div>
            <div onScroll={handleCityUsersScroll} style={{ flex: 1, overflowY: "auto", paddingTop: "8px" }}>
                {cityUsers.length === 0 && !cityUsersLoading ? (
                    <p className="text-sm m-0" style={{ color: "#9ca3af", textAlign: "center", padding: "24px 0" }}>
                        No users found
                    </p>
                ) : (
                    cityUsers.map((u: any) => (
                        <div
                            key={u.id}
                            className="flex items-center gap-2 cursor-pointer"
                            style={{ padding: "6px 0", borderBottom: "1px solid #e5e7eb" }}
                            onClick={() => navigate(`/admin/profiles/view/${u.id}`)}
                        >
                            <img
                                src={u.profileimg || `${process.env.REACT_APP_API_URL}/public/userprofile/default.png`}
                                alt={u.name}
                                style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#f3f4f6" }}
                            />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <p className="m-0 text-xs font-medium" style={{ color: "#264164", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {u.name} {u.surname}
                                </p>
                                <p className="m-0" style={{ color: "#6b7280", fontSize: "11px" }}>{u.countrycode} {u.mobileno}</p>
                                {u.ip && (
                                    <p className="m-0 flex items-center" style={{ color: "#9ca3af", fontSize: "10px", fontFamily: "monospace" }}>
                                        <CountryFlag countryCode={u.ipcountry} size={11} />
                                        {u.ip}
                                    </p>
                                )}
                            </div>
                            {u.lastactiveago && (
                                <div style={{ textAlign: "center", flexShrink: 0, minWidth: 70 }}>
                                    <p className="m-0" style={{ color: "#264164", fontSize: "11px", fontWeight: 500 }}>{u.lastactiveago}</p>
                                    {u.lastactivecity && (
                                        <p className="m-0" style={{ color: "#9ca3af", fontSize: "10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                            <CountryFlag countryCode={u.lastactiveipcountry} size={11} />
                                            {u.lastactivecity}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
                {cityUsersLoading && (
                    <div className="flex flex-col gap-2" style={{ padding: "8px 0" }}>
                        {Array.from({ length: 3 }).map((_, i) => (
                            <Skeleton key={i} height="2rem" className="rounded" />
                        ))}
                    </div>
                )}
            </div>
        </div>
    ) : null;

    const getRangeParam = (id: string) => {
        switch (id) {
            case "7d": return "7days";
            case "1m": return "1month";
            case "1y": return "1year";
            case "fb": return "all";
            default: return "all";
        }
    };

    const fetchDashboardData = async (rangeId: string) => {
        const range = getRangeParam(rangeId);
        try {
            const response = await apiFunctions.get(`/user/dashboard?range=${range}`);
            setTotalUsers(response.data.totalUsers || 0);
            setMobileOnlyUsers(response.data.mobileOnlyUsers || 0);
            setGrandTotalUsers(response.data.grandTotalUsers || 0);
            setIsRealUsers(response.data.isRealUsers || 0);
            setApprovedUsers(response.data.approvedUsers || 0);
            setNewLeads(response.data.newLeads || 0);
            setTotalBusinesses(response.data.totalBusinesses || 0);
            setTotalProducts(response.data.totalProducts || 0);
            setTotalActiveProducts(response.data.totalActiveProducts || 0);
            setTotalJobs(response.data.totalJobs || 0);
            setTotalMatrimonialProfiles(response.data.totalMatrimonialProfiles || 0);
            setLatestLeads(response.data.latestLeads || []);
        } catch (error) {
            console.error("Failed to load dashboard data:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDashboardData(countryid || "fb");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [countryid]);

    // Activity heatmap data — refetched when range changes
    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get(`/user/dashboard/charts?range=${activityRange}`);
                if (response?.data?.success) {
                    setActivityData(response.data.activity || []);
                }
            } catch (error) {
                console.error("Failed to load chart data:", error);
            }
        })();
    }, [activityRange]);

    // Monthly new-users chart data — fetched once
    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get("/user/dashboard/charts");
                if (response?.data?.success) {
                    setMonthlyData(response.data.monthly || []);
                }
            } catch (error) {
                console.error("Failed to load chart data:", error);
            }
        })();
    }, []);

    // Registered users grouped by current location — fetched once
    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get("/user/dashboard/locations");
                if (response?.data?.success) {
                    setLocations(response.data.locations || []);
                    setTotalWithLocation(response.data.totalWithLocation || 0);
                }
            } catch (error) {
                console.error("Failed to load location data:", error);
            } finally {
                setLocationsLoading(false);
            }
        })();
    }, []);

    // Site-wide active users (last 30 min + today) — same numbers echoed on
    // the Active Users pages, fetched once
    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get("/activeusers/summary");
                if (response?.data?.success) {
                    setActiveUsers30Min(response.data.last30Min ?? 0);
                    setActiveUsersToday(response.data.today ?? 0);
                }
            } catch (error) {
                console.error("Failed to load active users summary:", error);
            }
        })();
    }, []);

    // Play Store rating/reviews/installs — same stats shown on the Play
    // Store Reviews page; limit=1 since only `stats` is needed here
    useEffect(() => {
        (async () => {
            try {
                const response = await apiFunctions.get("/playreviews?limit=1");
                if (response?.data?.success) {
                    setPlayStats(response.data.data.stats || { averageRating: null, totalReviews: null, installs: null });
                }
            } catch (error) {
                console.error("Failed to load Play Store stats:", error);
            }
        })();
    }, []);

    // Monthly new-users bar chart (chart.js)
    useEffect(() => {
        if (!monthlyCanvasRef.current) return;

        // build a full last-12-months series, filling missing months with 0
        const series: { label: string; count: number }[] = [];
        const now = new Date();
        for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const found = monthlyData.find(m => m.year === d.getFullYear() && m.month === d.getMonth() + 1);
            series.push({
                label: `${MONTH_NAMES[d.getMonth()]} ${String(d.getFullYear()).slice(2)}`,
                count: found?.count || 0,
            });
        }

        monthlyChartRef.current?.destroy();
        monthlyChartRef.current = new Chart(monthlyCanvasRef.current, {
            type: "bar",
            data: {
                labels: series.map(s => s.label),
                datasets: [{
                    label: "New Users",
                    data: series.map(s => s.count),
                    backgroundColor: "#2a8f9e",
                    borderRadius: 4,
                }],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false } },
                scales: {
                    y: { beginAtZero: true, ticks: { precision: 0, color: "#374151", font: { weight: "bold" as const } } },
                    x: { grid: { display: false }, ticks: { color: "#374151", font: { weight: "bold" as const } } },
                },
            },
        });

        return () => {
            monthlyChartRef.current?.destroy();
            monthlyChartRef.current = null;
        };
    }, [monthlyData]);

    const handleNewLeads = () => navigate("/admin/leads");

    const actionBodyTemplate = (rowData: any) => {
        return (
            <div className="action-icons">
                <Button label="View" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={() => handleView(rowData)} />
                {/* <Button label="Delete" link style={{ color: "#264164" }} onClick={() => handleDelete(rowData)} /> */}
            </div>
        );
    };

    const handleView = (rowData: any) => {
        console.log(rowData, "rowdata");
        navigate(`/admin/profiles/view/${rowData._id}`)
    };

    if (loading) {
        return (
            <div className="dashboard-margin">
                <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                    <div className="col-span-12 md:col-span-6">
                        <h2 className="card-title m-0">Dashboard</h2>
                    </div>
                    <div className="col-span-12 md:col-span-6 flex justify-start md:justify-end">
                        <div className="w-full md:w-64">
                            <Skeleton height="2.5rem" className="rounded" />
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 mt-4">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div className="col-span-12 lg:col-span-3" key={i}>
                            <div className="card h-full">
                                <Card>
                                    <Skeleton height="1rem" width="60%" className="rounded mb-2" />
                                    <Skeleton height="1.75rem" width="40%" className="rounded" />
                                </Card>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-12 lg:col-span-6">
                        <div className="card h-full">
                            <Card title="User Activity (Day × Hour)">
                                <Skeleton height="280px" className="rounded" />
                            </Card>
                        </div>
                    </div>
                    <div className="col-span-12 lg:col-span-6">
                        <div className="card h-full">
                            <Card title="New Users Per Month">
                                <Skeleton height="340px" className="rounded" />
                            </Card>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-4 mt-4">
                    <div className="col-span-12">
                        <div className="bg-white rounded-md shadow-sm p-3">
                            <Skeleton height="1.5rem" width="120px" className="rounded mb-3" />
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Skeleton key={i} height="2.5rem" className="rounded mb-2" />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-margin">
            <div className="grid grid-cols-12 gap-4 mt-4 items-center">
                {/* Title */}
                <div className="col-span-12 md:col-span-6">
                    <h2 className="card-title m-0">Dashboard</h2>
                </div>

                {/* Dropdown */}
                <div className="col-span-12 md:col-span-6 flex justify-start md:justify-end">
                    <div className="w-full md:w-64">
                        <DropdownComponent
                            id="country"
                            placeholder="Select Timeline"
                            modelValue={countryid}
                            options={selectionOptions}
                            onUpdateModelValue={(value: string) => setCountryId(value)}
                            showClear={true}
                        />
                    </div>
                </div>
            </div>


            {/* Cards Row */}
            <div className="grid grid-cols-12 gap-4 mt-4">

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Users">
                            <span className="text-2xl font-bold" style={{ color: "#264164" }}>{grandTotalUsers.toLocaleString()}</span>
                            <div className="flex flex-col gap-1 mt-2 text-xs">
                                <span className="px-2 py-1 rounded w-fit font-medium" style={{ background: "#ecfdf5", color: "#047857" }}>
                                    ✓ Registered: {totalUsers.toLocaleString()}
                                </span>
                                <span className="px-2 py-1 rounded w-fit font-medium" style={{ background: "#fffbeb", color: "#b45309" }}>
                                    📱 Mobile Only: {mobileOnlyUsers.toLocaleString()}
                                </span>
                                <span className="px-2 py-1 rounded w-fit font-medium" style={{ background: "#eef2ff", color: "#4338ca" }}>
                                    ★ Real Users: {isRealUsers.toLocaleString()}
                                </span>
                            </div>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Approved Users">
                            <span>{approvedUsers}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="New Leads">
                            <span>{newLeads}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Businesses">
                            <span>{totalBusinesses}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Matrimonial Profiles">
                            <span>{totalMatrimonialProfiles}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Products">
                            <span>{totalProducts}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Active Products">
                            <span>{totalActiveProducts}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Jobs">
                            <span>{totalJobs}</span>
                        </Card>
                    </div>
                </div>

            </div>

            {/* Play Store + Active Users Row */}
            <div className="grid grid-cols-12 gap-4 mt-4">

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Average Rating">
                            <span>{playStats.averageRating != null ? `${playStats.averageRating.toFixed(1)} ★` : "—"}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Reviews">
                            <span>{playStats.totalReviews != null ? playStats.totalReviews.toLocaleString() : "—"}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Total Installations">
                            <span>{playStats.installs || "—"}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Active Users (Last 30 Min)">
                            <span>{activeUsers30Min != null ? activeUsers30Min.toLocaleString() : "—"}</span>
                        </Card>
                    </div>
                </div>

                <div className="col-span-12 lg:col-span-3">
                    <div className="card h-full">
                        <Card title="Active Users Today">
                            <span>{activeUsersToday != null ? activeUsersToday.toLocaleString() : "—"}</span>
                        </Card>
                    </div>
                </div>

            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12 lg:col-span-6">
                    <div className="card h-full">
                        <Card title="User Activity (Day × Hour)">
                            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                                <p className="text-xs m-0" style={{ color: "#9ca3af" }}>
                                    darker = more active users
                                </p>
                                <div className="w-full sm:w-44">
                                    <DropdownComponent
                                        id="activityRange"
                                        modelValue={activityRange}
                                        options={activityRangeOptions}
                                        onUpdateModelValue={(value: string) => setActivityRange(value)}
                                    />
                                </div>
                            </div>
                            <ActivityHeatmap activity={activityData} />
                        </Card>
                    </div>
                </div>
                <div className="col-span-12 lg:col-span-6">
                    <div className="card h-full">
                        <Card title="New Users Per Month">
                            <p className="text-xs m-0 mb-2" style={{ color: "#9ca3af" }}>
                                Last 12 months
                            </p>
                            <div style={{ height: "340px" }}>
                                <canvas ref={monthlyCanvasRef} />
                            </div>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Registered Users by Location */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    <LocationMap
                        title="Registered Users by Location"
                        loading={locationsLoading}
                        locations={locations}
                        subtitle={`${totalWithLocation.toLocaleString()} user${totalWithLocation === 1 ? "" : "s"} with a saved current location`}
                        unitLabel="user"
                        onCityClick={handleCityClick}
                        citySidebarContent={citySidebarContent}
                    />
                </div>
            </div>

            {/* Table Row */}
            <div className="grid grid-cols-12 gap-4 mt-4">
                <div className="col-span-12">
                    {isMobile ? (
                        <div className="bg-white rounded-md shadow-sm p-3">
                            <h3 className="m-0 mb-2">New Leads</h3>
                            <div className="flex flex-col gap-2">
                                {latestLeads.length === 0 ? (
                                    <div style={{ textAlign: "center", padding: "64px 0" }}>
                                        <UserIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                        <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No new leads found</p>
                                    </div>
                                ) : (
                                    latestLeads.slice(0, 5).map((rowData: any, idx: number) => (
                                        <div key={rowData._id || idx} className="border rounded-lg p-3 w-full box-border" style={{ borderColor: "#e5e7eb" }}>
                                            <p className="m-0 font-semibold text-sm break-words" style={{ color: "#264164" }}>{rowData.name} {rowData.surname}</p>
                                            <div className="flex flex-col gap-1 mt-2 text-xs" style={{ color: "#4b5563" }}>
                                                <div><span className="font-medium">District:</span> {rowData.district}</div>
                                                <div>{formatDateTime(rowData.createddate)}</div>
                                            </div>
                                            <div className="flex justify-end gap-2 mt-2">
                                                {actionBodyTemplate(rowData)}
                                            </div>
                                        </div>
                                    ))
                                )}
                                {latestLeads.length > 0 && (
                                    <div className="flex justify-center mt-1">
                                        <Button label="Show More" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={handleNewLeads} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-md shadow-sm">
                            <DataTable
                                header={<h3 className="m-0">New Leads</h3>}
                                footer={
                                    latestLeads.length > 0 ? (
                                        <Button label="Show More" outlined size="small" style={{ color: "#264164", borderColor: "#264164" }} onClick={handleNewLeads} />
                                    ) : null
                                }
                                value={latestLeads}
                                emptyMessage={
                                    <div style={{ textAlign: "center", padding: "64px 0" }}>
                                        <UserIcon style={{ width: "48px", height: "48px", margin: "0 auto 12px", display: "block", color: "#d1d5db" }} />
                                        <p style={{ margin: 0, fontSize: "14px", fontWeight: 500, color: "#6b7280" }}>No new leads found</p>
                                    </div>
                                }
                                scrollable
                                resizableColumns
                                removableSort
                                rows={5}
                                style={{ minWidth: "600px" }}
                            >
                                <Column field="name" header="Name" sortable />
                                <Column field="surname" header="Surname" sortable />
                                <Column field="district" header="District" sortable />
                                <Column
                                    field="createddate"
                                    header="Joining Date"
                                    sortable
                                    body={(rowData) =>
                                        formatDateTime(rowData.createddate)
                                    }
                                />
                                <Column header="Action" className="action-column" body={actionBodyTemplate} />
                            </DataTable>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DashBoardPage;
